import { z } from 'zod'

const currentYear = new Date().getFullYear()

export const WARRANTY_ISSUE_TYPES = [
  'EDGE_LIFTING',
  'SEAM_SEPARATION',
  'BUBBLING',
  'ADHESION_FAILURE',
  'COLOUR_OR_FINISH',
  'DAMAGE',
  'OTHER',
] as const
export type WarrantyIssueTypeValue = (typeof WARRANTY_ISSUE_TYPES)[number]

export const WARRANTY_ISSUE_LABELS: Record<WarrantyIssueTypeValue, string> = {
  EDGE_LIFTING: 'Edge lifting',
  SEAM_SEPARATION: 'Seam separation',
  BUBBLING: 'Bubbling or wrinkling',
  ADHESION_FAILURE: 'Film not sticking',
  COLOUR_OR_FINISH: 'Colour, fade, or finish',
  DAMAGE: 'Damage (chip, scratch, tear)',
  OTHER: 'Something else',
}

export const WARRANTY_CLAIM_STATUSES = [
  'NEW',
  'UNDER_REVIEW',
  'INSPECTION_SCHEDULED',
  'APPROVED',
  'DECLINED',
  'RESOLVED',
] as const
export type WarrantyClaimStatusValue = (typeof WARRANTY_CLAIM_STATUSES)[number]

export const WARRANTY_STATUS_LABELS: Record<WarrantyClaimStatusValue, string> = {
  NEW: 'New',
  UNDER_REVIEW: 'Under review',
  INSPECTION_SCHEDULED: 'Inspection scheduled',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  RESOLVED: 'Resolved',
}

/** Photo limits enforced on both the client input and the server action. */
export const WARRANTY_PHOTO_LIMITS = {
  maxFiles: 4,
  maxBytes: 12 * 1024 * 1024,
  acceptedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'] as const,
} as const

/**
 * Text fields of a warranty claim. Photos arrive as File entries on the same
 * FormData and are validated separately in the action (zod has no File type in
 * the Node runtime).
 */
export const warrantyClaimSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  email: z.string().trim().email('Valid email is required'),
  phone: z.string().trim().min(7, 'A phone number is required').max(40),
  contactMethod: z.enum(['email', 'sms', 'call']).default('email'),
  vehicleYear: z.coerce
    .number()
    .int()
    .min(1900)
    .max(currentYear + 2)
    .optional()
    .transform((v) => (v && v > 0 ? v : undefined)),
  vehicleMake: z.string().trim().max(50).optional().or(z.literal('')),
  vehicleModel: z.string().trim().max(128).optional().or(z.literal('')),
  licencePlate: z.string().trim().max(12).optional().or(z.literal('')),
  // ISO date (YYYY-MM-DD) from a native date input; optional because a
  // customer may not remember and staff can pull it from the job file.
  installedOn: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter the install date as YYYY-MM-DD')
    .optional()
    .or(z.literal('')),
  jobReference: z.string().trim().max(60).optional().or(z.literal('')),
  issueType: z.enum(WARRANTY_ISSUE_TYPES).default('OTHER'),
  description: z
    .string()
    .trim()
    .min(20, 'Tell us a little more: where on the vehicle, and when you first noticed it')
    .max(5000),
  shopLocationId: z.string().cuid(),
  // Honeypot: real users never see or fill this field. Any value = bot. It is
  // deliberately not length-capped: a `max(0)` rule would fail validation and
  // hand the bot an error, and the action's pretend-success branch (the point
  // of a honeypot) would never be reached.
  website: z.string().optional(),
  // Cloudflare Turnstile token from the widget. Optional in the schema so an
  // unconfigured environment still parses; the action decides whether a
  // missing token blocks the submission.
  turnstileToken: z.string().optional(),
})

export type WarrantyClaimInput = z.input<typeof warrantyClaimSchema>
export type WarrantyClaimData = z.output<typeof warrantyClaimSchema>

export const warrantyClaimStatusUpdateSchema = z.object({
  id: z.string().cuid(),
  status: z.enum(WARRANTY_CLAIM_STATUSES),
  staffNotes: z.string().trim().max(4000).optional().or(z.literal('')),
})

export type WarrantyClaimStatusUpdateInput = z.input<typeof warrantyClaimStatusUpdateSchema>
